import { joinViewQuery, splitViewQuery } from 'components/tables/list-table/filter-bar/builtInViews';
import { VIRTUAL_MARKER } from 'components/tables/list-table/filterUtil';
import type { FigureRange } from 'types/homeWidgetFigures';
import type { JointFields } from './savedViewLists';

const OPERATOR = /^(?:==|=[a-zA-Z]+=)/;

/**
 * A view's query as the filter its list sends for it: the clauses of its main filters, then those of its quick
 * filters, then what the list's page adds to every request, joined by `;`. A clause is sent as the view's URL
 * carries it, but for the fields of `joint`, which the list folds as its `getJointFilter` does: the joined field's
 * own clause is left out, and its value goes into the clause of the field that hosts it.
 */
export function viewFilter(query: string, passiveFilter = '', joint: Record<string, JointFields> = {}): string {
    const params = new URLSearchParams(query);
    const clauses = [params.get('mf'), params.get('qf'), passiveFilter]
        .flatMap((part) => part?.split(';') ?? [])
        .map((clause) => (clause.startsWith(VIRTUAL_MARKER) ? clause.slice(VIRTUAL_MARKER.length) : clause))
        .filter(Boolean);
    const fields = Object.entries(joint);
    const isOn = (clause: string, selector: string) => clause.startsWith(`${selector}=`);
    return clauses
        .filter((clause) => !fields.some(([, { joinSelector }]) => isOn(clause, joinSelector)))
        .map((clause) => {
            // A hosting field's clause comes bare, or in the brackets its "is not" and "is empty" put around it.
            const hosted = fields.find(([host]) => isOn(clause, host) || isOn(clause, `(${host}`))?.[1];
            const joined = hosted && clauses.find((candidate) => isOn(candidate, hosted.joinSelector));
            if (!hosted || !joined) return clause;
            const value = joined.slice(hosted.joinSelector.length).replace(OPERATOR, '');
            const operator = hosted.comperator ?? '=lk=';
            return `(${clause};(${hosted.selectors.map((selector) => `${selector}${operator}${value}`).join(',')}))`;
        })
        .join(';');
}

/**
 * A view's query over `range`: every clause it has on the list's date field, an "is empty" one included, gives way
 * to the two a date range field writes. Its other filters and its sort stay.
 */
export function withDates(query: string, dateField: string, range: FigureRange): string {
    const { kept } = splitViewQuery(query, (clause) => !clause.startsWith(`${dateField}=`));
    const dates = `${dateField}=ge=${range.from};${dateField}=le=${range.to}`;
    return joinViewQuery(kept, new URLSearchParams({ mf: dates }).toString());
}
